import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  EnqueueVideoUploadOptions,
  OrgType,
  UploadJob,
  UploadJobStatus,
  UploadManagerContextValue,
} from '@/shared/domain'
import { reportError } from '@/shared/infra/observability'
import type { UploadGalleryVideo, UploadGalleryVideoStage } from '@/modules/gallery/use-cases'
import { UploadTray } from '@/shared/ui/components/upload-tray'
import { useToast } from './toast-context'
import { UploadManagerContext } from './upload-manager-context'

const MAX_CONCURRENT_UPLOADS = 2

type OrgContext = { orgId: string; orgType: OrgType }

type UploadManagerProviderProps = {
  onUploadGalleryVideo: UploadGalleryVideo
  children: React.ReactNode
}

export function UploadManagerProvider({
  onUploadGalleryVideo,
  children,
}: UploadManagerProviderProps) {
  const toast = useToast()
  const [jobs, setJobs] = useState<UploadJob[]>([])
  const [completedCount, setCompletedCount] = useState(0)

  const controllersRef = useRef<Map<string, AbortController>>(new Map())
  const filesRef = useRef<
    Map<string, { file: File; orgContext: OrgContext; album?: 'dogs' }>
  >(new Map())
  const startedRef = useRef<Set<string>>(new Set())
  const runningCountRef = useRef(0)

  const updateJob = useCallback((jobId: string, patch: Partial<UploadJob>) => {
    setJobs((prev) => prev.map((job) => (job.id === jobId ? { ...job, ...patch } : job)))
  }, [])

  const finishJob = useCallback((jobId: string, releaseFile = true) => {
    controllersRef.current.delete(jobId)
    if (releaseFile) filesRef.current.delete(jobId)
    runningCountRef.current = Math.max(0, runningCountRef.current - 1)
  }, [])

  const startJob = useCallback(
    (job: UploadJob) => {
      const entry = filesRef.current.get(job.id)
      if (!entry) return

      startedRef.current.add(job.id)
      runningCountRef.current += 1

      const controller = new AbortController()
      controllersRef.current.set(job.id, controller)
      updateJob(job.id, { status: 'generating' })

      const stageToStatus: Record<UploadGalleryVideoStage, UploadJobStatus> = {
        generating: 'generating',
        uploading: 'uploading',
        recording: 'recording',
      }

      onUploadGalleryVideo({
        file: entry.file,
        orgId: entry.orgContext.orgId,
        orgType: entry.orgContext.orgType,
        album: entry.album,
        signal: controller.signal,
        onStage: (stage) => updateJob(job.id, { status: stageToStatus[stage] }),
      })
        .then(() => {
          finishJob(job.id)
          updateJob(job.id, { status: 'success' })
          setCompletedCount((count) => count + 1)
          toast.success('Video uploaded', entry.file.name)
        })
        .catch((err) => {
          finishJob(job.id, false)
          if (controller.signal.aborted) return
          const message = reportError(err, {
            fallbackMessage: 'Upload failed. Please try again.',
          })
          updateJob(job.id, { status: 'error', error: message })
          toast.error('Upload failed', message)
        })
    },
    [finishJob, onUploadGalleryVideo, toast, updateJob],
  )

  useEffect(() => {
    if (runningCountRef.current >= MAX_CONCURRENT_UPLOADS) return
    const next = jobs.find((job) => job.status === 'queued' && !startedRef.current.has(job.id))
    if (next) startJob(next)
  }, [jobs, startJob])

  const enqueueVideoUpload = useCallback(
    (file: File, orgContext: OrgContext, options?: EnqueueVideoUploadOptions) => {
      const id = crypto.randomUUID()
      const album = options?.album
      filesRef.current.set(id, { file, orgContext, album })
      setJobs((prev) => [
        ...prev,
        { id, fileName: file.name, status: 'queued', orgContext, album },
      ])
    },
    [],
  )

  const cancelUpload = useCallback(
    (jobId: string) => {
      controllersRef.current.get(jobId)?.abort()
      finishJob(jobId)
      startedRef.current.delete(jobId)
      setJobs((prev) => prev.filter((job) => job.id !== jobId))
    },
    [finishJob],
  )

  const retryUpload = useCallback((jobId: string) => {
    const entry = filesRef.current.get(jobId)
    setJobs((prev) => prev.filter((job) => job.id !== jobId))
    startedRef.current.delete(jobId)
    filesRef.current.delete(jobId)
    if (!entry) return
    const id = crypto.randomUUID()
    filesRef.current.set(id, entry)
    setJobs((prev) => [
      ...prev,
      {
        id,
        fileName: entry.file.name,
        status: 'queued',
        orgContext: entry.orgContext,
        album: entry.album,
      },
    ])
  }, [])

  const dismissJob = useCallback((jobId: string) => {
    startedRef.current.delete(jobId)
    filesRef.current.delete(jobId)
    setJobs((prev) => prev.filter((job) => job.id !== jobId))
  }, [])

  const value = useMemo<UploadManagerContextValue>(
    () => ({
      jobs,
      completedCount,
      enqueueVideoUpload,
      cancelUpload,
      retryUpload,
      dismissJob,
    }),
    [jobs, completedCount, enqueueVideoUpload, cancelUpload, retryUpload, dismissJob],
  )

  return (
    <UploadManagerContext.Provider value={value}>
      {children}
      <UploadTray
        jobs={jobs}
        onCancel={cancelUpload}
        onRetry={retryUpload}
        onDismiss={dismissJob}
      />
    </UploadManagerContext.Provider>
  )
}
